<template>
  <BContainer>
    <BRow>
      <BCol lg="6" class="pr-lg-5">
        <cld-image public-id="v1557870122/state_of_visual_media/cloudinary-slide3.svg" fluid alt="A plant and a steaming mug next to a computer on a desk with media on the screen and a social “like” emerging from it" />
        <h2 class="my-4">How and when your story is viewed affects the experience</h2>
        <p>The experience of a well-crafted visual story will ultimately be determined through how it’s viewed. Understanding how and when your visual story will be viewed—on which devices and what types of browsers or clients—will enable you to create visuals that engage more effectively with your audience.</p>
      </BCol>
      <BCol lg="6" class="pl-lg-5">
        <strong class="d-block mb-3">How your story is being viewed</strong>
        <BRow class="mb-3">
          <BCol cols="4" class="text-center" align-h="center">
            <ChartDonut :num="numbers.desktop" class="chart-donut mb-2" chart-id="donut-desktop" />
            <p>Desktop</p>
          </BCol>
          <BCol cols="4" class="text-center" align-h="center">
            <ChartDonut :num="numbers.mobile" class="chart-donut mb-2" chart-id="donut-mobile" />
            <p>Mobile</p>
          </BCol>
        </BRow>
        <BRow>
          <BCol>
            <strong>When your story is being viewed</strong>
            <ul class="d-flex">
              <li>Desktop Web</li>
              <li>Mobile Web</li>
            </ul>
            <ChartLine :chartdata="chartableDesktopMobile" :stack="true" :height="chartHeight" chart-id="chartableDesktopMobile" />
          </BCol>
        </BRow>
        <v-popover popover-class="pp-60">
          <LearnMore />
          <template slot="popover">
            <div class="popover-title">
              <cld-image class="tooltip-finger-icon" public-id="v1557870041/state_of_visual_media/cloudinary-finger.svg" alt="Icon of a hand with the index finger pointing up, representing a tool tip" />
              <p>
                Ensuring your visuals have an effective user experience is essential to crafting a compelling visual story—and it’s also important to make sure you have a strong visual narrative.
                <strong>Here are some tips:</strong>
              </p>
            </div>
            <b-row class="ml-2">
              <b-col sm="4" class="num-outer px-sm-3">
                <div class="num">1</div>
                <p>Select powerful visuals that evoke emotion</p>
              </b-col>
              <b-col sm="4" class="num-outer px-sm-3">
                <div class="num">2</div>
                <p>Build a narrative arc, placing the viewer at the center</p>
              </b-col>
              <b-col sm="4" class="num-outer px-sm-3">
                <div class="num">3</div>
                <p>Demonstrate authenticity</p>
              </b-col>
              <b-col sm="4" class="num-outer px-sm-3">
                <div class="num">4</div>
                <p>Let your brand personality shine</p>
              </b-col>
              <b-col sm="4" class="num-outer px-sm-3">
                <div class="num">5</div>
                <p>Use video</p>
              </b-col>
            </b-row>
          </template>
        </v-popover>
      </BCol>
    </BRow>
  </BContainer>
</template>

<script>
import LearnMore from "../LearnMore.vue";
import ChartDonut from "../donut-filled.vue";
import ChartLine from "../line.vue";

export default {
  components: {
    LearnMore,
    ChartDonut,
    ChartLine
  },
  props: {
    numbers: {
      type: Object,
      default: () => {
        return {};
      }
    },
    dataDesktopMobile: {
      type: [Object, Array],
      default: () => [0]
    }
  },
  computed: {
    chartableDesktopMobile() {
      let desktop = Object.values(this.dataDesktopMobile[0]);
      desktop.splice(-1, 1);
      desktop = desktop.map(function(v) {
        return v * 100;
      });
      // console.log(desktop);
      let mobile = Object.values(this.dataDesktopMobile[1]);
      mobile.splice(-1, 1);
      mobile = mobile.map(function(v) {
        return v * 100;
      });
      const color1 = "#0091ff";
      const color2 = "#f4b21b";
      // console.log(mobile);
      let transformed = {
        datasets: [
          {
            label: "Desktop Web",
            data: desktop,
            backgroundColor: `rgba(${this.hexToRgb(color1)}, 0.75)`,
            borderColor: color1,
            pointBorderColor: color1,
            pointRadius: 0,
            borderWidth: 2,
            fill: true
          },
          {
            label: "Mobile Web",
            data: mobile,
            backgroundColor: `rgba(${this.hexToRgb(color2)}, 0.75)`,
            borderColor: color2,
            pointBorderColor: color2,
            pointRadius: 0,
            borderWidth: 2,
            fill: true
          }
        ],
        labels: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24]
      };

      this.dataDesktopMobile;

      return transformed;
    },
    chartHeight() {
      const winWidth = window.innerWidth;
      if (winWidth > 500) {
        return 360;
      }
      return 240;
    }
  }
};
</script>

<style lang="scss" scoped>
.num {
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  background: $blue;
  border-radius: 50%;
  $dim: 36px;
  width: $dim;
  height: $dim;
  font-size: 20px;
  font-weight: 700;
  margin-top: 0.5em;
  margin-bottom: 0.5em;
  flex-shrink: 0;
}
.num-outer {
  @include media-breakpoint-down(sm) {
    display: flex;
    align-items: flex-start;
    .num {
      margin-right: 0.5em;
    }
    p {
      margin-top: 1em;
    }
  }
}

ul {
  list-style: none;
  margin: 0;
  margin-top: 0.5em;
  padding: 0;
  li {
    margin-right: 2em;
    &:before {
      $dim: 12px;
      height: $dim;
      width: $dim;
      display: inline-block;
      margin-right: 0.25em;
      content: "";
    }
    &:nth-of-type(1):before {
      background: $blue;
    }
    &:nth-of-type(2):before {
      background: $gold;
    }
  }
}
</style>
